import img_close_cross from "../../../img/closeCrossIcon.svg";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useRef, useState, useEffect } from "react";
import { 
  Button, 
  Divider, 
  Box, 
  Paper, 
  Typography, 
  Stack, 
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Avatar,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { ChooseLanguageDropdown } from "../../layout/header/choose-language-dropdown";
import { useHistory } from "react-router-dom";
import { SSORegisterFields } from "./sso-register-fields";
import { SSORegisterSocialButtons } from "./sso-register-social-buttons";
import { useSidebarStore } from "../../../store/sidebarStore";
import { showErrorToast } from "../../../toast/toast-service";

export const SSORegisterSidebar = () => {
  const { ssoRegisterSidebar: ssoState, setSsoRegisterSidebar: setSsoState } = useSidebarStore();
  const { isOpen, provider } = ssoState;
  const location = useLocation();

  // Close sidebar when navigating to a different page
  useEffect(() => {
    if (isOpen) {
      setSsoState({ isOpen: false, provider: "" });
    }
  }, [location.pathname, setSsoState]);
  const [formValues, setFormValues] = useState({
    university: "",
    degreeProgram: "",
    areAGBsChecked: false,
  });
  const [formValueErrors, setFormValueErrors] = useState({
    university: false,
    degreeProgram: false,
    areAGBsChecked: false,
  });

  const intl = useIntl();
  const history = useHistory();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const universityRef = useRef(null);
  const degreeProgramRef = useRef(null);

  const closeSidebar = () => {
    history.push("/");
    setSsoState({ isOpen: false, provider: "" });
  };

  const checkFormFilledForSocial = () => {
    // Check if all required fields are filled
    const isUniversityValid = formValues.university !== "";
    const isDegreeProgramValid = formValues.degreeProgram !== "";
    const isAGBValid = formValues.areAGBsChecked;

    // Show validation errors for all invalid fields
    if (!isUniversityValid) {
      setFormValueErrors(prev => ({
        ...prev,
        university: intl.formatMessage({ id: "login-errors-general-required" })
      }));
    }
    
    if (!isDegreeProgramValid) {
      setFormValueErrors(prev => ({
        ...prev,
        degreeProgram: intl.formatMessage({ id: "login-errors-general-required" })
      }));
    }
    
    if (!isAGBValid) {
      const agbError = intl.formatMessage({ id: "registration.AGBs-checked.error" });
      setFormValueErrors(prev => ({
        ...prev,
        areAGBsChecked: agbError
      }));
      showErrorToast(agbError);
    }

    // Focus on first invalid field
    if (!isUniversityValid) {
      universityRef.current.focus();
    } else if (!isDegreeProgramValid) {
      degreeProgramRef.current.focus();
    }

    return isUniversityValid && isDegreeProgramValid && isAGBValid;
  };

  return (
    <Slide direction="left" in={isOpen} timeout={400}>
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          height: '100vh',
          height: '100dvh', // Dynamic viewport height for mobile
          width: isDesktop ? '420px' : '100%',
          maxWidth: isDesktop ? '420px' : '100%',
          top: 0,
          right: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
          zIndex: 13,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          // Mobile-specific improvements
          '@media (max-width: 768px)': {
            height: '100vh',
            height: '100dvh',
            paddingBottom: 'env(safe-area-inset-bottom)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #E87975 0%, #F86B6B 50%, #FF8181 100%)',
            zIndex: 1,
          }
        }}
        data-testid="sso-register-sidebar"
      >
        {/* Header */}
        <Fade in={isOpen} timeout={600}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '24px 32px',
              background: 'linear-gradient(135deg, rgba(232, 121, 117, 0.05) 0%, rgba(248, 107, 107, 0.05) 100%)',
              borderBottom: '1px solid rgba(232, 121, 117, 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #E87975 0%, #F86B6B 100%)',
                  fontSize: '18px',
                  fontWeight: 'bold',
                }}
              >
                B
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '20px',
                    background: 'linear-gradient(135deg, #E87975 0%, #F86B6B 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.2,
                  }}
                  data-testid="sso-register-sidebar-title"
                >
                  <FormattedMessage id={`sign-up.continue-with-${provider}`} />
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.grey[600],
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  Complete your registration
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={closeSidebar}
              data-testid="sso-register-sidebar-close-button"
              sx={{ 
                padding: '12px',
                backgroundColor: 'rgba(232, 121, 117, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(232, 121, 117, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <img 
                src={img_close_cross} 
                alt="Close"
                style={{ width: '20px', height: '20px' }}
              />
            </IconButton>
          </Box>
        </Fade>

        {/* Main Content - Scrollable */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(232, 121, 117, 0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(232, 121, 117, 0.3)',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(232, 121, 117, 0.5)',
          },
        }}>
          <Fade in={isOpen} timeout={800}>
            <Box sx={{ 
              padding: isDesktop ? '32px' : '24px', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 3,
              minHeight: '100%',
            }}>
              <SSORegisterFields
                formValues={formValues}
                formValueErrors={formValueErrors}
                setFormValueErrors={setFormValueErrors}
                setFormValues={setFormValues}
                refs={{
                  universityRef,
                  degreeProgramRef,
                }}
              />

              <SSORegisterSocialButtons
                provider={provider}
                formValues={formValues}
                checkFormFilledForSocial={checkFormFilledForSocial}
                closeSidebar={closeSidebar}
              />

              {/* Footer - Scrollable */}
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ borderColor: 'rgba(232, 121, 117, 0.1)', mb: 3 }} />
                <Stack
                  direction="column"
                  alignItems="center"
                  spacing={2}
                  sx={{ 
                    padding: isDesktop ? '0 0 24px 0' : '0 0 20px 0',
                    background: 'linear-gradient(135deg, rgba(232, 121, 117, 0.02) 0%, rgba(248, 107, 107, 0.02) 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                  }}
                >
                  <ChooseLanguageDropdown />
                  <Button
                    component={Link}
                    to="/help"
                    variant="text"
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: theme.palette.grey[600],
                      textTransform: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(232, 121, 117, 0.1)',
                        color: '#E87975',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <FormattedMessage id="menu.info-links.help" />
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Paper>
    </Slide>
  );
};

